

import { Directionality } from '@angular/cdk/bidi';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  Optional,
  Output,
  Renderer2,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';

import { zoomBigMotion } from 'mwc/core/animation';
import { isPresetColor, MwcPresetColor } from 'mwc/core/color';
import { MwcNoAnimationDirective } from 'mwc/core/no-animation';
import { BooleanInput, NgStyleInterface, MwcTSType } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';

import {
  isTooltipEmpty,
  MwcTooltipBaseComponent,
  MwcTooltipBaseDirective,
  MwcTooltipTrigger,
  PropertyMapping
} from './base';

@Directive({
  selector: '[mwc-tooltip]',
  exportAs: 'mwcTooltip',
  host: {
    '[class.mwc-tooltip-open]': 'visible'
  }
})
export class MwcTooltipDirective extends MwcTooltipBaseDirective {
  static ngAcceptInputType_mwcTooltipArrowPointAtCenter: BooleanInput;

  @Input('mwcTooltipTitle') override title?: MwcTSType | null;
  @Input('mwcTooltipTitleContext') titleContext?: Object | null = null;
  @Input('mwc-tooltip') override directiveTitle?: MwcTSType | null;
  @Input('mwcTooltipTrigger') override trigger?: MwcTooltipTrigger = 'hover';
  @Input('mwcTooltipPlacement') override placement?: string | string[] = 'top';
  @Input('mwcTooltipOrigin') override origin?: ElementRef<HTMLElement>;
  @Input('mwcTooltipVisible') override visible?: boolean;
  @Input('mwcTooltipMouseEnterDelay') override mouseEnterDelay?: number;
  @Input('mwcTooltipMouseLeaveDelay') override mouseLeaveDelay?: number;
  @Input('mwcTooltipOverlayClassName') override overlayClassName?: string;
  @Input('mwcTooltipOverlayStyle') override overlayStyle?: NgStyleInterface;
  @Input('mwcTooltipArrowPointAtCenter') @InputBoolean() override arrowPointAtCenter?: boolean;
  @Input() mwcTooltipColor?: string;

  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('mwcTooltipVisibleChange') override readonly visibleChange = new EventEmitter<boolean>();

  override componentRef: ComponentRef<MwcToolTipComponent> = this.hostView.createComponent(MwcToolTipComponent);

  constructor(
    elementRef: ElementRef,
    hostView: ViewContainerRef,
    resolver: ComponentFactoryResolver,
    renderer: Renderer2,
    @Host() @Optional() noAnimation?: MwcNoAnimationDirective
  ) {
    super(elementRef, hostView, resolver, renderer, noAnimation);
  }

  protected override getProxyPropertyMap(): PropertyMapping {
    return {
      ...super.getProxyPropertyMap(),
      mwcTooltipColor: ['mwcColor', () => this.mwcTooltipColor],
      mwcTooltipTitleContext: ['mwcTitleContext', () => this.titleContext]
    };
  }
}

@Component({
  selector: 'mwc-tooltip',
  exportAs: 'mwcTooltipComponent',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [zoomBigMotion],
  template: `
    <ng-template
      #overlay="cdkConnectedOverlay"
      cdkConnectedOverlay
      mwcConnectedOverlay
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayOpen]="_visible"
      [cdkConnectedOverlayPositions]="_positions"
      [cdkConnectedOverlayPush]="true"
      [mwcArrowPointAtCenter]="mwcArrowPointAtCenter"
      (overlayOutsideClick)="onClickOutside($event)"
      (detach)="hide()"
      (positionChange)="onPositionChange($event)"
    >
      <div
        class="mwc-tooltip"
        [class.mwc-tooltip-rtl]="dir === 'rtl'"
        [ngClass]="_classMap"
        [ngStyle]="mwcOverlayStyle"
        [@.disabled]="!!noAnimation?.mwcNoAnimation"
        [mwcNoAnimation]="noAnimation?.mwcNoAnimation"
        [@zoomBigMotion]="'active'"
      >
        <div class="mwc-tooltip-content">
          <div class="mwc-tooltip-arrow">
            <span class="mwc-tooltip-arrow-content" [ngStyle]="_contentStyleMap"></span>
          </div>
          <div class="mwc-tooltip-inner" [ngStyle]="_contentStyleMap">
            <ng-container *mwcStringTemplateOutlet="mwcTitle; context: mwcTitleContext">{{ mwcTitle }}</ng-container>
          </div>
        </div>
      </div>
    </ng-template>
  `,
  preserveWhitespaces: false
})
export class MwcToolTipComponent extends MwcTooltipBaseComponent {
  override mwcTitle: MwcTSType | null = null;
  mwcTitleContext: Object | null = null;

  mwcColor?: string | MwcPresetColor;

  _contentStyleMap: NgStyleInterface = {};

  constructor(
    cdr: ChangeDetectorRef,
    @Optional() directionality: Directionality,
    @Host() @Optional() noAnimation?: MwcNoAnimationDirective
  ) {
    super(cdr, directionality, noAnimation);
  }

  protected isEmpty(): boolean {
    return isTooltipEmpty(this.mwcTitle);
  }

  protected override updateStyles(): void {
    const isColorPreset = this.mwcColor && isPresetColor(this.mwcColor);

    this._classMap = {
      [this.mwcOverlayClassName]: true,
      [`${this._prefix}-placement-${this.preferredPlacement}`]: true,
      [`${this._prefix}-${this.mwcColor}`]: isColorPreset
    };

    this._contentStyleMap = {
      backgroundColor: !!this.mwcColor && !isColorPreset ? this.mwcColor : null
    };
  }
}
